import React, { useEffect, useState } from 'react';
import { Loader2, Package, Truck, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { getUserOrders } from '../../services/api';
import type { Order } from '../../services/api';
import type { Creation } from '../../types/create';
import { OrderDetailView } from './OrderDetailView';

interface OrderDetailsProps {
  selectedCreation: Creation | undefined | null;
  loadingProducts?: boolean;
}

export const OrderDetails: React.FC<OrderDetailsProps> = ({ selectedCreation, loadingProducts }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const fetchedOrders = await getUserOrders(false);
        setOrders(fetchedOrders);
      } catch (err) {
        console.error("Failed to fetch orders", err);
        setError("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Filter orders for the current creation
  const filteredOrders = orders.filter(order => {
    if (!selectedCreation) return false;
    
    // Check if order has creation_id or creation_hash in metadata
    const creationIdMeta = order.metadata?.find(m => m.key === 'creation_id' || m.key === 'creation_hash');
    if (creationIdMeta) {
        return creationIdMeta.value === selectedCreation.id;
    }

    return false;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-4" />
        <p className="text-gray-500">Loading your orders...</p>
      </div>
    );
  }

  if (selectedOrder) {
      return (
          <OrderDetailView 
             order={selectedOrder} 
             selectedCreation={selectedCreation} 
             onBack={() => setSelectedOrder(null)} 
          />
      );
  }

  if (loadingProducts && selectedCreation && (!selectedCreation.products || selectedCreation.products.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-4" />
        <p className="text-gray-500">Loading creation details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-500">
        <AlertCircle className="w-8 h-8 mb-4" />
        <p>{error}</p>
      </div>
    );
  }

  if (!selectedCreation) {
     return (
        <div className="text-center py-12 text-gray-500">
            <p>Please select a creation to view its orders.</p>
        </div>
     );
  }

  if (filteredOrders.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h3>
        <p className="text-gray-500">You haven't placed any orders for this creation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
        <Package className="w-5 h-5" />
        Order History
      </h2>
      
      {filteredOrders.map(order => (
        <div 
            key={order.id} 
            onClick={() => setSelectedOrder(order)}
            className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm cursor-pointer hover:border-gray-300 transition-colors group"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 border-b border-gray-100 pb-4">
            <div>
              <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-500">Order ID</p>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
              <p className="font-mono font-medium text-gray-900">{order.id}</p>
            </div>
            <div className="mt-2 md:mt-0 text-right">
              <p className="text-sm text-gray-500">Date Placed</p>
              <p className="font-medium text-gray-900">
                {order.createdAtServer ? new Date(order.createdAtServer).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
               {order.fulfillmentStatus === 'shipped' ? (
                   <Truck className="w-4 h-4 text-green-600" />
               ) : order.fulfillmentStatus === 'canceled' ? (
                   <AlertCircle className="w-4 h-4 text-red-600" />
               ) : (
                   <Clock className="w-4 h-4 text-blue-600" />
               )}
               <span className="text-sm font-medium capitalize">
                 {order.fulfillmentStatus ? order.fulfillmentStatus.replace('_', ' ') : 'Processing'}
               </span>
            </div>
            {order.shipment && (
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {order.shipment.trackingUrl && (
                        <a href={order.shipment.trackingUrl} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Track Shipment
                        </a>
                    )}
                    {order.shipment.carrierName && <span className="ml-2">via {order.shipment.carrierName}</span>}
                </div>
            )}
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Items</h4>
            {order.items.map((item, idx) => {
               // Try to find product details from the creation's products
               const product = selectedCreation.products.find(p => (p.gelatoProduct?.productUid || p.id) === item.productUid);
               const productName = product ? (product.gelatoProduct?.title || product.name) : 'Custom Product';
               const previewUrl = item.files[0]?.url || product?.image;

               return (
                   <div key={idx} className="flex items-center gap-4">
                       <div className="w-12 h-12 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                           {previewUrl && <img src={previewUrl} alt={productName} className="w-full h-full object-cover" />}
                       </div>
                       <div>
                           <p className="font-medium text-gray-900 line-clamp-1">{productName}</p>
                           <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                       </div>
                   </div>
               );
            })}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
             <span className="font-medium text-gray-900">Total</span>
             <span className="text-blue-600 text-sm font-medium group-hover:underline">
                View Details
             </span>
          </div>
        </div>
      ))}
    </div>
  );
};
